/**
 * Taking the money.
 *
 * THE CHANGE CALCULATION IS THE POINT
 * -----------------------------------
 * A cashier doing this in their head, at speed, with a queue, is where money
 * goes missing — and it goes missing in the customer's favour or the shop's,
 * unpredictably. Showing the change is the single most useful thing this
 * screen does.
 *
 * The quick amounts are the notes people actually hand over. Tapping "1000"
 * is faster and less error-prone than typing it.
 *
 * THE AMOUNT IS THE SERVER'S, NOT THE TILL'S
 * ------------------------------------------
 * Change is calculated against the total the server quoted, which includes
 * tax. An earlier version used the cart's subtotal, and at the rate configured
 * today that is 16% low — on a Rs. 999 order the cashier would hand back
 * Rs. 160 too much, every time, with nothing on screen looking wrong.
 *
 * The same pricing code answers the quote and charges the order, so the figure
 * shown here is the figure taken.
 */
import { useState } from 'react';
import { formatPKR, round2 } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';
import { SplitPanel } from './SplitPanel.jsx';
import { ManagerApproval } from './ManagerApproval.jsx';
import { needsApproval } from './approvalCodes.js';

/** The notes in circulation, largest last so the common ones come first. */
const QUICK_AMOUNTS = [500, 1000, 2000, 5000];

export function PaymentSheet({ quote, orderType, staff = [], onConfirm, onCancel, busy, error }) {
  const base = quote?.totalAmount ?? 0;
  const rates = quote?.taxRates ?? null;

  /**
   * A delivery is not being paid for at this counter.
   *
   * The sheet used to ask "cash, card or online" on every order and treat any
   * answer as money received. On a delivery that is a guess about something
   * that has not happened yet — the food has not left the shop. The question
   * worth asking is whether the customer has already settled it online, or
   * whether the rider collects at the door.
   *
   * Card is not offered: there is no card to take over the telephone.
   */
  const goingOut = orderType === 'delivery';

  const [splitting, setSplitting] = useState(false);
  const [method, setMethod] = useState('cash');
  const [prepaid, setPrepaid] = useState(false);
  const [tendered, setTendered] = useState('');
  const [staffId, setStaffId] = useState('');

  /**
   * Instead of taking money, put the bill on a staff member's account.
   *
   * Not offered on a delivery — that has its own way of being owed for. The
   * list of who a meal can go on is passed in, so the cashier can choose even
   * though they cannot open the full staff screen.
   */
  const onStaff = !goingOut && method === 'staff';
  const staffList = goingOut ? [] : staff;

  /**
   * The total for the tender chosen. Cash and online carry the cash rate; card
   * carries its own, which is lower here. The server prices the charge the same
   * way, so this is the figure that will actually be taken — showing it lets the
   * cashier see the card total before confirming, and gives the right amount to
   * make change against.
   */
  const priceFor = (m) => {
    const rate = rates ? (m === 'card' ? rates.card : rates.cash) : null;
    if (rate == null) return { total: base, tax: quote?.taxAmount ?? 0 };
    // A fresh order's quote carries the taxable base and any delivery fee.
    if (quote?.taxableAmount != null) {
      const tax = round2((quote.taxableAmount * rate) / 100);
      return { total: round2(quote.taxableAmount + tax + (quote.deliveryFee || 0)), tax };
    }
    // A bill taken earlier (dine-in, no delivery): its total was quoted at the
    // cash rate, so scale it to the tender's rate.
    const denom = 100 + (rates.cash || 0);
    const total = round2((base * (100 + rate)) / denom);
    const taxable = round2((base * 100) / denom);
    return { total, tax: round2(total - taxable) };
  };
  const priced = priceFor(goingOut ? 'cash' : method);
  const amount = priced.total;

  /**
   * What is being paid, built once.
   *
   * The approval retry has to send the identical payment — a manager's PIN
   * against a different amount than the one refused would be approving
   * something nobody looked at.
   */
  const payment = () =>
    goingOut
      ? {
          // How it will be settled, and whether it already has been. Without
          // the flag the branch leaves the money owing, which is what sends
          // the rider out knowing the figure to collect.
          paymentMethod: prepaid ? 'online' : 'cash',
          ...(prepaid ? { prepaid: true } : {}),
        }
      : onStaff
        ? // No money now — it goes on the staff member's account to clear later.
          { staffAccountId: staffId }
        : {
            paymentMethod: method,
            ...(method === 'cash' && Number(tendered) > 0 ? { paidAmount: Number(tendered) } : {}),
          };

  const given = Number(tendered) || 0;
  const change = round2(given - amount);
  // A delivery takes nothing at the counter, and a staff account takes no money
  // at all — it just needs a name. Otherwise cash has to cover the bill.
  const enough = goingOut
    ? true
    : onStaff
      ? Boolean(staffId)
      : method !== 'cash' || given >= amount;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Take payment"
      className="fixed inset-0 z-20 flex items-end justify-center bg-black/70 sm:items-center"
    >
      <div className="w-full max-w-md rounded-t-2xl bg-surface p-5 sm:rounded-2xl">
        {splitting ? (
          <SplitPanel
            total={amount}
            busy={busy}
            error={error}
            onCancel={() => setSplitting(false)}
            onConfirm={onConfirm}
          />
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <h2 className="font-semibold text-lg text-ink">Take payment</h2>
              <span className="text-2xl font-semibold tabular-nums text-ink">
                {formatPKR(amount)}
              </span>
            </div>
            {priced.tax > 0 && (
              <p className="mt-0.5 text-xs text-ink-3">
                Includes {formatPKR(priced.tax)} tax
                {quote?.deliveryFee > 0 && ` and ${formatPKR(quote.deliveryFee)} delivery`}
              </p>
            )}

            {goingOut ? (
              <>
                <div role="group" aria-label="How this delivery is settled" className="mt-4 flex gap-1.5">
                  {[
                    { value: false, label: 'Rider collects' },
                    { value: true, label: 'Paid online' },
                  ].map((option) => (
                    <button
                      key={String(option.value)}
                      aria-pressed={prepaid === option.value}
                      onClick={() => setPrepaid(option.value)}
                      className={`h-12 flex-1 rounded-lg text-sm font-medium transition-colors ${
                        prepaid === option.value
                          ? 'bg-brand-400 text-surface-2'
                          : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <p className="mt-3 text-sm text-ink-3">
                  {prepaid
                    ? 'Already settled. The rider will be told to collect nothing.'
                    : `The rider will be told to collect ${formatPKR(amount)} at the door.`}
                </p>
              </>
            ) : (
              <div role="group" aria-label="Payment method" className="mt-4 flex gap-1.5">
                {[
                  { value: 'cash', label: 'Cash' },
                  { value: 'card', label: 'Card' },
                  { value: 'online', label: 'Online' },
                  { value: 'staff', label: 'Staff' },
                ].map((option) => (
                  <button
                    key={option.value}
                    aria-pressed={method === option.value}
                    onClick={() => setMethod(option.value)}
                    className={`h-12 flex-1 rounded-lg text-sm font-medium transition-colors ${
                      method === option.value
                        ? 'bg-brand-400 text-surface-2'
                        : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {!goingOut && method === 'cash' && (
              <div className="mt-4">
                <label htmlFor="tendered" className="mb-1.5 block text-sm font-medium text-ink-2">
                  Cash received
                </label>
                <input
                  id="tendered"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  autoFocus
                  value={tendered}
                  onChange={(e) => setTendered(e.target.value)}
                  placeholder="0"
                  className="h-14 w-full rounded-lg border border-rule bg-surface-3 px-3.5 text-ink text-xl font-semibold tabular-nums focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
                />

                <div className="mt-2 flex gap-1.5">
                  {QUICK_AMOUNTS.map((value) => (
                    <button
                      key={value}
                      onClick={() => setTendered(String(value))}
                      className="h-11 flex-1 rounded-lg bg-surface-2 text-sm font-medium tabular-nums text-ink-2 hover:bg-rule"
                    >
                      {value}
                    </button>
                  ))}
                  <button
                    onClick={() => setTendered(String(Math.ceil(amount)))}
                    className="h-11 flex-1 rounded-lg bg-surface-2 text-sm font-medium text-ink-2 hover:bg-rule"
                  >
                    Exact
                  </button>
                </div>

                {given > 0 && (
                  <div
                    className={`mt-3 flex items-baseline justify-between rounded-lg px-3.5 py-3 ${
                      change >= 0 ? 'bg-good-bg' : 'bg-warn-bg'
                    }`}
                  >
                    <span
                      className={`text-sm font-medium ${change >= 0 ? 'text-good' : 'text-warn'}`}
                    >
                      {change >= 0 ? 'Change to give' : 'Still owed'}
                    </span>
                    <span
                      className={`text-2xl font-semibold tabular-nums ${
                        change >= 0 ? 'text-good' : 'text-warn'
                      }`}
                    >
                      {formatPKR(Math.abs(change))}
                    </span>
                  </div>
                )}
              </div>
            )}

            {onStaff && (
              <div className="mt-4">
                <label htmlFor="staff" className="mb-1.5 block text-sm font-medium text-ink-2">
                  Whose account?
                </label>
                <select
                  id="staff"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
                >
                  <option value="">Choose a staff member or account…</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {Number(s.account_only) ? ' · account' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-sm text-ink-3">
                  No money is taken now. {formatPKR(amount)} goes on their account to clear later.
                </p>
              </div>
            )}

            {needsApproval(error) ? (
              <ManagerApproval
                error={error}
                busy={busy}
                onApprove={(approval) => onConfirm({ ...payment(), approval })}
              />
            ) : (
              error && (
                <p
                  role="alert"
                  className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad"
                >
                  {error.message}
                </p>
              )
            )}

            <div className="mt-5 flex gap-2">
              <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
                Back
              </Button>
              {/* Two people splitting it, or a card that came up short. Common
              enough that without it the cashier rings two orders and the
              kitchen makes two tickets. */}
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setSplitting(true)}
                // A staff account is not split across tenders — it is one tab.
                disabled={busy || onStaff}
              >
                Split
              </Button>
              <Button
                size="lg"
                className="flex-1"
                busy={busy}
                // Taking less cash than the order costs is almost always a typo,
                // and it silently leaves the drawer short at the end of the shift.
                disabled={!enough}
                onClick={() => onConfirm(payment())}
              >
                {busy ? 'Placing order' : onStaff ? 'Put on account' : 'Complete order'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
